"use client"

import * as React from "react"
import { X } from "lucide-react"
import { Badge } from "./badge"
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "./command"

export interface Option {
  value: string
  label: string
}

interface MultiSelectProps {
  options: Option[]
  selected: string[]
  onChange: (selected: string[]) => void
  placeholder?: string
  className?: string
}

export function MultiSelect({
  options,
  selected,
  onChange,
  placeholder = "Select options",
  className,
}: MultiSelectProps) {
  const [open, setOpen] = React.useState(false)
  const [inputValue, setInputValue] = React.useState("")

  const handleUnselect = (option: string) => {
    onChange(selected.filter((s) => s !== option))
  }

  const selectables = options.filter((option) => !selected.includes(option.value))

  return (
    <div className={className}>
      <div
        className="flex flex-wrap gap-1 border border-input rounded-md px-3 py-2 text-sm ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2"
        onClick={() => setOpen(true)}
      >
        {selected.length > 0 && (
          <>
            {selected.map((option) => {
              const selectedOption = options.find((o) => o.value === option)
              return (
                <Badge key={option} variant="secondary" className="mr-1 mb-1">
                  {selectedOption?.label}
                  <button
                    className="ml-1 rounded-full outline-none ring-offset-background focus:ring-2 focus:ring-ring focus:ring-offset-2"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleUnselect(option)
                      }
                    }}
                    onMouseDown={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                    }}
                    onClick={(e) => {
                      e.stopPropagation()
                      handleUnselect(option)
                    }}
                  >
                    <X className="h-3 w-3" />
                    <span className="sr-only">Remove {selectedOption?.label}</span>
                  </button>
                </Badge>
              )
            })}
          </>
        )}
        <input
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onFocus={() => setOpen(true)}
          className="flex-1 bg-transparent outline-none placeholder:text-muted-foreground"
          placeholder={selected.length === 0 ? placeholder : ""}
        />
      </div>
      {open && selectables.length > 0 && (
        <div className="relative mt-2">
          <Command className="absolute top-0 w-full z-10 rounded-md border bg-popover text-popover-foreground shadow-md outline-none animate-in">
            <CommandInput
              placeholder={`Search ${placeholder.toLowerCase()}...`}
              value={inputValue}
              onValueChange={setInputValue}
            />
            <CommandList>
              <CommandEmpty>No option found.</CommandEmpty>
              <CommandGroup>
                {selectables.map((option) => (
                  <CommandItem
                    key={option.value}
                    onSelect={() => {
                      onChange([...selected, option.value])
                      setInputValue("")
                      setOpen(false)
                    }}
                    className="cursor-pointer"
                  >
                    {option.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </div>
      )}
    </div>
  )
}
